/* eslint-disable react-refresh/only-export-components -- context module exports hooks + provider */
import { type ReactNode, createContext, useContext, useMemo } from "react";

export type UiColorPalette = {
  /** Most washed tinted surface */
  softer: string;
  /** Stronger wash (soft / outline / ghost hover) */
  soft: string;
  /** Opaque filled control (pastel for named colours) */
  solid: string;
  /** Solid hover — emphasize `solid` */
  strong: string;
  /** Solid active — further emphasize `solid` */
  stronger: string;
  /** Foreground on `solid` / `strong` / `stronger` */
  onSolid: string;
  /**
   * Colour-as-text / border / icon on light backgrounds.
   * Readable vs white, or equal to `solid` when `solid` already is.
   */
  ink: string;
};

/** Shared size tokens used by Input and other chrome that scales children. */
export type UISize = "small" | "medium" | "large";

export type UIContextValue = { colors?: UiColorPalette; size?: UISize };

export const asUIContextValue = (props: {
  [key in keyof UIContextValue]?: UIContextValue[key] | null;
}): UIContextValue | null => {
  if (Object.keys(props).length === 0) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(props).filter(([_, value]) => value !== null),
  ) as UIContextValue;
};

const UIContext = createContext<UIContextValue | null>(null);

type UIProviderProps = {
  /** Partial values merge over the nearest parent UI context. */
  value: Partial<UIContextValue>;
  children: ReactNode;
};

/**
 * Provide optional UI tokens (`colors`, `size`, …). Nested providers only
 * override the keys they set; other keys inherit from the parent.
 */
export function UIProvider({ value, children }: UIProviderProps) {
  const parent = useContext(UIContext);
  const merged = useMemo(
    (): UIContextValue => ({
      colors: value.colors !== undefined ? value.colors : parent?.colors,
      size: value.size !== undefined ? value.size : parent?.size,
    }),
    [value.colors, value.size, parent?.colors, parent?.size],
  );

  return <UIContext.Provider value={merged}>{children}</UIContext.Provider>;
}

/** Optional full UI context — null outside any provider. */
export function useUIOptional(): UIContextValue | null {
  return useContext(UIContext);
}

/** Read nearest colors. Throws if colors are not provided. */
export function useUiColor(): UiColorPalette {
  const colors = useContext(UIContext)?.colors;
  if (!colors) {
    throw new Error(
      "useUiColor() requires colors via a UIProvider in the component tree",
    );
  }
  return colors;
}

/** Optional colors — null when unset. */
export function useUiColorOptional(): UiColorPalette | null {
  return useContext(UIContext)?.colors ?? null;
}
